import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Link as LinkIcon, Users, Plus, MessageSquare, 
  ChevronRight, Phone, ArrowRight, ShieldCheck, Sparkles 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchSocialLinks, fetchMyProfile } from '../../services/profile';
import { fetchCurrentUser } from '../../services/auth';
import { api } from '../../services/api';
import type { SubscriptionPeriod, SubscriptionPlan, SubscriptionStatus } from '../../services/subscriptions';

interface SubscriptionInfo {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  period: SubscriptionPeriod | null;
  expiresAt: string | null;
  remainingDays: number | null;
}

const INITIAL_SUBSCRIPTION: SubscriptionInfo = {
  plan: 'free',
  status: 'active',
  period: null,
  expiresAt: null,
  remainingDays: null,
};

// --- CRM Leads helpers ---

interface HomeLead {
  id: number;
  name: string;
  email: string | null;
  mobile_number: string;
  subject: string;
  message: string;
  is_read: number;
  created_at: string;
}

type HomeLeadStatus = 'contacted' | 'follow_up' | 'converted' | 'lost';

const getStoredStatus = (leadId: number): HomeLeadStatus | null => {
  const stored = localStorage.getItem(`lead_status_${leadId}`);
  if (stored === 'contacted' || stored === 'follow_up' || stored === 'converted' || stored === 'lost') {
    return stored;
  }
  return null;
};

const setStoredStatus = (leadId: number, status: HomeLeadStatus | null): void => {
  if (status) {
    localStorage.setItem(`lead_status_${leadId}`, status);
  } else {
    localStorage.removeItem(`lead_status_${leadId}`);
  }
};

const HOME_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new: { label: 'New', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  follow_up: { label: 'Follow-up', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700 border-green-200' },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-700 border-red-200' },
};

const toWhatsAppUrl = (mobile: string): string => {
  const digits = mobile.replace(/[^\d+]/g, '');
  return `https://wa.me/${digits}`;
};

const Home: React.FC = () => {
  const { user } = useAuth();
  const [socialLinksCount, setSocialLinksCount] = useState<number | null>(null);
  const [collaborationCount, setCollaborationCount] = useState<number | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo>(INITIAL_SUBSCRIPTION);
  const [recentLeads, setRecentLeads] = useState<HomeLead[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<Record<number, HomeLeadStatus | null>>({});

  useEffect(() => {
    let isMounted = true;

    const loadDashboardData = async () => {
      try {
        const [socialRes, profileRes, authRes, leadsRes] = await Promise.allSettled([
          fetchSocialLinks(),
          fetchMyProfile(),
          fetchCurrentUser(),
          api.get('/contact-leads'),
        ]);

        if (!isMounted) return;

        if (socialRes.status === 'fulfilled' && socialRes.value.data.success) {
          const count = socialRes.value.data.count ?? socialRes.value.data.social_links.length;
          setSocialLinksCount(count);
        } else {
          setSocialLinksCount(null);
        }

        if (profileRes.status === 'fulfilled' && profileRes.value.data.success) {
          setCollaborationCount(profileRes.value.data.profile.collaborations?.length ?? 0);
        } else {
          setCollaborationCount(null);
        }

        if (authRes.status === 'fulfilled' && authRes.value.data.success) {
          const authUser = authRes.value.data.user;
          setSubscription({
            plan: authUser.subscription_plan,
            status: authUser.subscription_status,
            period: authUser.subscription_period,
            expiresAt: authUser.subscription_expires_at,
            remainingDays: calculateRemainingDays(authUser.subscription_expires_at),
          });
        } else {
          setSubscription(INITIAL_SUBSCRIPTION);
        }

        if (leadsRes.status === 'fulfilled' && leadsRes.value.data.success) {
          const allLeads: HomeLead[] = leadsRes.value.data.leads || [];
          const topLeads = allLeads.slice(0, 4);
          setRecentLeads(topLeads);
          const statuses: Record<number, HomeLeadStatus | null> = {};
          topLeads.forEach(l => { statuses[l.id] = getStoredStatus(l.id); });
          setLeadStatuses(statuses);
        }
      } catch (error) {
        console.error('[Dashboard] Failed to load data', error);
        if (isMounted) {
          setSocialLinksCount(null);
          setCollaborationCount(null);
          setSubscription(INITIAL_SUBSCRIPTION);
        }
      }
    };

    loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  const updateHomeLeadStatus = (leadId: number, newStatus: HomeLeadStatus) => {
    setStoredStatus(leadId, newStatus);
    setLeadStatuses(prev => ({ ...prev, [leadId]: newStatus }));
    toast.success(`Status updated to ${HOME_STATUS_CONFIG[newStatus]?.label || newStatus}`);
  };




  return (
    <div className="bg-[#FAFAFA] min-h-[calc(100vh-4rem)] p-4 sm:p-6 lg:p-8 font-inter">
      <div className="max-w-[1200px] mx-auto space-y-6">
        
        {/* Welcome Banner */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl p-8 sm:p-10 text-white shadow-lg shadow-blue-900/10">
          <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 opacity-20 pointer-events-none">
            <svg width="400" height="400" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFFFFF" d="M45.7,-76.1C58.9,-69.3,69.2,-55.4,78.2,-40.8C87.2,-26.2,94.9,-10.9,94.4,4.2C93.9,19.3,85.2,34.2,74.5,46.9C63.8,59.6,51.1,70.1,36.5,77.1C21.9,84.1,5.4,87.6,-10.3,86.2C-26,84.8,-40.9,78.5,-53.4,69.5C-65.9,60.5,-76.1,48.8,-82.1,34.9C-88.1,21,-89.9,4.9,-86,-10.1C-82.1,-25,-72.5,-38.8,-60.7,-49.6C-48.9,-60.4,-34.9,-68.2,-20.9,-73.4C-6.9,-78.6,7.1,-81.2,21.5,-80.6C35.9,-80,50.7,-76.2,45.7,-76.1Z" transform="translate(100 100) scale(1.1)" />
            </svg>
          </div>
          
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-blue-200" />
                <span className="text-xs font-bold text-blue-200 uppercase tracking-widest">Dashboard Overview</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2 tracking-tight text-white">
                Welcome back, {user?.name?.split(' ')[0] ?? 'there'}!
              </h1>
              <p className="text-blue-100 text-sm sm:text-base font-medium max-w-xl">
                Here's what's happening with your MyOneQR profile today. 
                Monitor your connected links and manage incoming leads effortlessly.
              </p>
            </div>
            
            <div className="inline-flex w-fit flex-shrink-0 items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-xl mt-2 md:mt-0">
              <div className="bg-white/20 p-3 rounded-xl">
                <ShieldCheck className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-200 font-medium uppercase tracking-wider">Current Plan</p>
                <p className="text-lg font-bold capitalize text-white">{subscription.plan}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <StatCard
            icon={LinkIcon}
            label="Social Links"
            value={formatCount(socialLinksCount)}
            caption="Active connections on your profile"
            iconColor="text-blue-600"
            iconBg="bg-blue-50"
            quickAction={{ label: "Add Link", to: "/dashboard/profile" }}
          />
          <StatCard
            icon={Users}
            label="Proud Member Of"
            value={formatCount(collaborationCount)}
            caption="Organizations you collaborate with"
            iconColor="text-purple-600"
            iconBg="bg-purple-50"
            quickAction={{ label: "Add Collaboration", to: "/dashboard/profile" }}
          />
        </div>

        {/* Recent Leads Section */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
          <div className="p-6 sm:p-8 flex items-center justify-between border-b border-gray-50">
            <div>
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">Recent Leads</h2>
              <p className="text-sm text-gray-500 mt-1">Latest messages and inquiries from your profile.</p>
            </div>
            <Link
              to="/dashboard/leads"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-50 hover:bg-gray-100 text-sm font-semibold text-gray-700 transition-colors border border-gray-200"
            >
              View all leads
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {recentLeads.length === 0 ? (
            <div className="p-12 text-center flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <MessageSquare className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No recent leads</h3>
              <p className="text-sm text-gray-500">When visitors contact you, they'll appear here.</p>
            </div>
          ) : (
            <div className="w-full">
              {/* Mobile Stack */}
              <div className="md:hidden p-4 space-y-3 bg-gray-50/50">
                {recentLeads.map((lead) => {
                  const status = leadStatuses[lead.id] ?? null;
                  const statusKey = status || 'new';
                  const cfg = HOME_STATUS_CONFIG[statusKey];
                  return (
                    <div key={lead.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-bold text-gray-900">{lead.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5 font-medium">{formatDate(lead.created_at)}</p>
                        </div>
                        <select
                          value={status || 'new'}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val !== 'new') updateHomeLeadStatus(lead.id, val as HomeLeadStatus);
                          }}
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border outline-none appearance-none cursor-pointer ${cfg.color}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="follow_up">Follow-up</option>
                          <option value="converted">Converted</option>
                          <option value="lost">Lost</option>
                        </select>
                      </div>
                      
                      <div className="space-y-1.5 mb-4">
                        {lead.email && <p className="text-sm text-gray-600 truncate flex items-center gap-2"><div className="w-1 h-1 rounded-full bg-gray-300"/>{lead.email}</p>}
                        <p className="text-sm text-gray-600 truncate flex items-center gap-2"><div className="w-1 h-1 rounded-full bg-gray-300"/>{lead.mobile_number}</p>
                        <p className="text-sm font-medium text-gray-800 truncate mt-2 bg-gray-50 p-2 rounded-lg border border-gray-100">Sub: {lead.subject}</p>
                      </div>

                      <div className="flex items-center gap-2 pt-3 border-t border-gray-50">
                        <a href={`tel:${lead.mobile_number}`} className="flex-1 flex justify-center items-center py-2 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 transition" title="Call">
                          <Phone className="h-4 w-4" />
                        </a>
                        <a href={toWhatsAppUrl(lead.mobile_number)} target="_blank" rel="noopener noreferrer" className="flex-1 flex justify-center items-center py-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#128C7E] rounded-xl transition" title="WhatsApp">
                          <MessageSquare className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  );
                })}
                <Link to="/dashboard/leads" className="block w-full text-center py-3 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 mt-2">
                  View full list
                </Link>
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-y border-gray-100">
                    <tr>
                      <th className="px-6 py-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Contact Details</th>
                      <th className="px-6 py-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Subject</th>
                      <th className="px-6 py-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 font-semibold text-gray-500 text-xs uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {recentLeads.map((lead) => {
                      const status = leadStatuses[lead.id] ?? null;
                      const statusKey = status || 'new';
                      const cfg = HOME_STATUS_CONFIG[statusKey];
                      return (
                        <tr key={lead.id} className="hover:bg-gray-50/50 transition-colors group">
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-gray-900">{lead.name}</span>
                              <span className="text-xs text-gray-500 mt-0.5">{lead.email || lead.mobile_number}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 max-w-[200px]">
                            <span className="truncate block text-gray-700 font-medium">{lead.subject}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="relative inline-block">
                                <select
                                  value={status || 'new'}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val !== 'new') updateHomeLeadStatus(lead.id, val as HomeLeadStatus);
                                  }}
                                  className={`appearance-none text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border outline-none cursor-pointer pr-6 ${cfg.color}`}
                                >
                                  <option value="new">New</option>
                                  <option value="contacted">Contacted</option>
                                  <option value="follow_up">Follow-up</option>
                                  <option value="converted">Converted</option>
                                  <option value="lost">Lost</option>
                                </select>
                                <ChevronRight className="h-3 w-3 absolute right-2 top-1/2 -translate-y-1/2 text-current opacity-50 pointer-events-none rotate-90" />
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-500 text-xs font-medium whitespace-nowrap">
                              {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <a href={`tel:${lead.mobile_number}`} className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition" title="Call">
                                <Phone className="h-4 w-4" />
                              </a>
                              <a href={toWhatsAppUrl(lead.mobile_number)} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#128C7E] rounded-lg transition" title="WhatsApp">
                                <MessageSquare className="h-4 w-4" />
                              </a>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

// Sub-components

const StatCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  caption: string;
  iconBg: string;
  iconColor: string;
  quickAction?: { label: string; to: string };
}> = ({ icon: Icon, label, value, caption, iconBg, iconColor, quickAction }) => (
  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8 hover:shadow-md hover:border-gray-200 transition-all duration-300 relative group overflow-hidden">
    <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full ${iconBg} opacity-0 group-hover:opacity-50 blur-3xl transition-opacity duration-500`} />
    <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-12 h-12 rounded-2xl ${iconBg} border border-white/50 flex items-center justify-center shadow-inner`}>
            <Icon className={`h-6 w-6 ${iconColor}`} />
          </div>
          {quickAction && (
            <Link to={quickAction.to} className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors border border-gray-200">
              <Plus className="h-3 w-3" />
              <span>Add</span>
            </Link>
          )}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</h3>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-gray-900 tracking-tight">{value}</span>
          </div>
          <p className="text-sm text-gray-500 mt-2 font-medium">{caption}</p>
        </div>
    </div>
  </div>
);

const formatCount = (value: number | null) => (value === null ? '—' : value.toString());

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

const calculateRemainingDays = (expiresAt: string | null) => {
  if (!expiresAt) return null;
  const end = new Date(expiresAt);
  if (Number.isNaN(end.getTime())) return null;
  const diff = end.getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export default Home;
